import { BigNumber } from 'ethers';

export type Address = string;
export type QueryArgs = Record<string, any>;
export type QueryAttrs = Record<string, any>;
export type QueryBuilder = (
  args?: QueryArgs,
  attrs?: QueryAttrs
) => Record<string, any>;

export enum PoolType {
  Weighted = 'Weighted',
  Investment = 'Investment',
  Stable = 'Stable',
  MetaStable = 'MetaStable',
  StablePhantom = 'StablePhantom',
  LiquidityBootstrapping = 'LiquidityBootstrapping',
  SingleFarm = 'SingleFarm'
}
export type TimeTravelPeriod = '24h';

export interface PoolToken {
  address: string;
  balance: string;
  weight: string;
  priceRate: string | null;
  symbol?: string;
}

export interface RawPoolTokens {
  balances: BigNumber[];
  lastChangeBlock: BigNumber;
  tokens: string[];
}

export interface Pool {
  id: string;
  address: string;
  poolType: PoolType;
  swapFee: string;
  owner: string;
  factory: string;
  tokens: PoolToken[];
  tokensList: string[];
  tokenAddresses: string[];
  totalLiquidity: string;
  miningTotalLiquidity: string;
  totalShares: string;
  totalSwapFee: string;
  totalSwapVolume: string;
  hasLiquidityMiningRewards: boolean;
  onchain?: OnchainPoolData;
  createTime: number;
  mainTokens?: string[];
  wrappedTokens?: string[];
  linearPoolTokensMap?: Record<string, PoolToken>;
  unwrappedTokens?: string[];
  basicRewardsEachDay?: string;
  isFarmPool?: boolean;
  totalLpStaked?: string;
  isSingleFarm?: boolean;
  isMetaBoosted?: boolean;
  metaBoostedParentTokens?: string[];
  metaBoostedPoolTokens?: Record<string, RawPoolTokens>;
}

export interface BalancersType {
  totalLiquidity: string;
  poolCount: string;
  id: string;
  totalSwapCount: string;
  totalSwapFee: string;
  totalSwapVolume: string;
}

export interface FarmPool extends Pool {
  pid: string;
  lpToken: string;
  allocPoint: string;
  totalLpStaked: string;
  isDualFarming: boolean;
  rewarder: string;
  dualRewarderToken: string[];
  dualRewardPerSecond: string[];
  boostToken: string | null;
  totalBoostStaked: string | null;
  investPool: Pool | null | undefined;
  basicRewardsEachDay: string | undefined;
  isSingleFarm: boolean | undefined;
  pendingFlake?: string;
  basicAPR?: string;
  poolTokens?: RawPoolTokens;
}

export interface FarmingInfo {
  totalAllocPoint: string;
  flakePerSecond: string;
  flake: string;
  booster: string;
  poolCount: string;
}

export interface FarmUserInfo extends FarmPool {
  userAccount: string;
  lpStaked: string;
  boostDecimals?: string;
  boostRatio?: string;
  boostStaked?: string;
}

export interface BaseFlakeInfos {
  convertor: string;
  flake: string;
  id: string;
  leavingTerm: string;
  rate: string;
  totalStakedFlake: string;
}

export interface FlakeCirculates {
  circulateBal: string;
  timeStamp: string;
  ratioOflocked: string;
  lockedFlake: string;
}

export interface UserLeaveRequests {
  flakeAmount: string;
  index: string;
  id: string;
  timestamp: string;
  user: string;
  veFlakeAmount: string;
}

export interface UserFlakeInfos {
  pendingFirstIndex: string;
  pendingFlake: string;
  id: string;
  stakedFlake: string;
  user: string;
  veFlakeBalance: string;
}

export interface LinearPool extends Pool {
  mainIndex: number;
  wrappedIndex: number;
}

export interface DecoratedPool extends Pool {
  dynamic: {
    period: TimeTravelPeriod;
    volume: string;
    apr: {
      pool: string;
      thirdParty: string;
      thirdPartyBreakdown: { [address: string]: string };
      liquidityMining: string;
      liquidityMiningBreakdown: { [address: string]: string };
      total: string;
    };
    fees: string;
    isNewPool: boolean;
  };
  totalLpStaked?: string;
  lpStaked?: string;
  pendingFlake?: string;
  pid?: string;
  basicRewardsEachDay?: string;
  totalBoostStaked?: string;
  shares?: string;
  basicAPR?: string;
}

export interface OnchainTokenData {
  balance: string;
  weight: number;
  decimals: number;
  logoURI: string | undefined;
  name: string;
  symbol: string;
}

export type OnchainTokenDataMap = Record<Address, OnchainTokenData>;

export interface RawOnchainPoolData {
  decimals: number;
  poolTokens: RawPoolTokens;
  swapFee: BigNumber;
  totalSupply: BigNumber;
  weights?: BigNumber[];
  swapEnabled?: boolean;
  amp?: {
    value: BigNumber;
    precision: BigNumber;
  };
  linearPools?: Record<Address, RawLinearPoolData>;
  tokenRates?: BigNumber[];
  metaLinearPools?: Record<Address, RawLinearPoolData>;
}

export interface OnchainPoolData {
  tokens: Record<Address, OnchainTokenData>;
  totalSupply: string;
  decimals: number;
  swapFee: string;
  amp?: string;
  swapEnabled: boolean;
  linearPools?: Record<Address, LinearPoolData>;
  tokenRates?: string[];
  metaLinearPools?: Record<Address, LinearPoolData>;
}

export interface RawLinearPoolToken {
  address: string;
  index: BigNumber;
}

export interface RawWrappedLinearPoolToken extends RawLinearPoolToken {
  rate: string;
}

export interface LinearPoolToken {
  address: string;
  index: number;
  balance: string;
}

export interface WrappedLinearPoolToken extends LinearPoolToken {
  priceRate: string;
}

export interface RawLinearPoolData {
  id: string;
  priceRate: BigNumber;
  mainToken: RawLinearPoolToken;
  wrappedToken: RawWrappedLinearPoolToken;
  unwrappedTokenAddress: string;
  totalSupply: string;
  tokenData: RawPoolTokens;
}
export type RawLinearPoolDataMap = Record<Address, RawLinearPoolData>;

export interface LinearPoolData {
  id: string;
  priceRate: string;
  mainToken: LinearPoolToken;
  wrappedToken: WrappedLinearPoolToken;
  unwrappedTokenAddress: string;
  totalSupply: string;
}
export type LinearPoolDataMap = Record<Address, LinearPoolData>;

export interface FullPool extends DecoratedPool {
  onchain: OnchainPoolData;
}

export type AnyPool = Pool | FullPool | DecoratedPoolWithShares | FarmPool;

export interface PoolShare {
  poolId: {
    id: string;
  };
  balance: string;
}

export interface DecoratedPoolWithShares extends DecoratedPool {
  shares: string;
  basicRewardsEachDay?: string;
  userShares?: string;
}

export type PoolActivityType = 'Join' | 'Exit';

export interface PoolActivity {
  amounts: string[];
  timestamp: number;
  tx: string;
  type: PoolActivityType;
}

export interface PoolSwap {
  tokenIn: string;
  tokenOut: string;
  tokenAmountIn: string;
  tokenAmountOut: string;
  timestamp: number;
  tx: string;
}

export interface PoolSnapshot {
  pool: {
    id: string;
  };
  timestamp: number;
  amounts: string[];
  totalShares: string;
  swapVolume: string;
  swapFees: string;
  liquidity: string;
}

export type PoolSnapshots = Record<number, PoolSnapshot>;

export type TradePairSnapshot = {
  timestamp: number;
  totalSwapFee: string;
  totalSwapVolume: string;
  pair: {
    token0: {
      symbol: string;
      address: string;
    };
    token1: {
      symbol: string;
      address: string;
    };
  };
};
